import React, { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Global event bus for cross-component notification refresh
export const refreshNotifications = () => {
  window.dispatchEvent(new Event('notifications:refresh'));
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    const handler = () => fetchNotifications();
    window.addEventListener('notifications:refresh', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications:refresh', handler);
    };
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markRead = async (id) => {
    await fetch(`${BACKEND_URL}/api/notifications/${id}/read`, {
      method: 'PATCH', credentials: 'include'
    });
    fetchNotifications();
  };

  const markAllRead = async () => {
    await fetch(`${BACKEND_URL}/api/notifications/read-all`, {
      method: 'POST', credentials: 'include'
    });
    fetchNotifications();
  };

  const typeIcon = (type) => {
    const map = {
      job_accepted: '✓',
      job_started: '▶',
      job_completed: '★',
      job_cancelled: '✕',
      rating_received: '★',
      payment_received: 'R'
    };
    return map[type] || '•';
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="notification-bell-btn"
          className="relative bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full w-10 h-10 p-0 btn-hover flex items-center justify-center"
        >
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1"
            >
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0 max-h-[500px] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">Notifications</h3>
          {unreadCount > 0 && (
            <button
              data-testid="mark-all-read-btn"
              onClick={markAllRead}
              className="text-xs text-teal-700 hover:text-teal-800 font-medium flex items-center gap-1"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all read
            </button>
          )}
        </div>
        <div className="overflow-y-auto flex-1">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Bell className="w-12 h-12 mx-auto mb-2 text-slate-300" strokeWidth={1.5} />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.notification_id}
                data-testid={`notification-${n.notification_id}`}
                onClick={() => !n.read && markRead(n.notification_id)}
                className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                  !n.read ? 'bg-teal-50/50' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                    !n.read ? 'bg-teal-700 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {typeIcon(n.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm">{n.title}</p>
                    <p className="text-slate-600 text-sm mt-1">{n.message}</p>
                    <p className="text-slate-400 text-xs mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                  {!n.read && <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-2"></div>}
                </div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
